<template>
  <tr class="odd:bg-white even:bg-gray-100 child:p-3 text-left text-xs">
    <td>
      <div
        class="flex items-center space-x-2"
        :style="{ paddingLeft: `${(depth * 16) + 8}px` }"
      >
        <template v-if="showEditButtons">
          <button
            v-if="!isEditing"
            type="button"
            class="btn-icon btn-icon-red"
            @click="$emit('delete')"
          >
            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
          </button>

          <button
            v-if="isEditing"
            type="button"
            class="btn-icon btn-icon-red"
            @click="isEditing = false"
          >
            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
          </button>

          <button
            v-else
            type="button"
            class="btn-icon btn-icon-blue"
            @click="edit"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
          </button>

          <button
            v-if="isEditing"
            type="button"
            class="btn-icon-green btn-icon"
            @click="updatePackageTradeItem"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
          </button>
        </template>

        <p
          class="w-60 text-wrap"
          :class="{ 'pl-2': !showEditButtons }"
        >
          <span class="font-semibold text-gray-900">{{ $props.packageTradeItem.billItem }}</span> {{ $props.packageTradeItem.trade_item.name }}
        </p>
      </div>
    </td>

    <td
      v-if="isEditing"
    >
      <div class="flex items-center space-x-2">
        <AppTypeAhead
          v-model="wbsCodeSearch"
          class="form-input p-1 text-xs"
          name="wbs_code"
          :suggestion-callback="getSuggestions"
          :initial-suggestions="getSuggestions"
          show-empty-suggestions
          :scrollable-container="scrollableContainer"
          @value-was-selected="assignWbsCode"
        >
          <template #emptyText>
            <p class="text-sm text-gray-500">
              No WBS codes match.
            </p>
          </template>
        </AppTypeAhead>
      </div>
    </td>

    <td
      v-else
    >
      {{ $props.packageTradeItem.wbs_code?.code || '' }}
    </td>

    <td class="text-right">
      {{ australianNumber($props.packageTradeItem.trade_item.quantity) }}
      ({{ $props.packageTradeItem.trade_item.unit }})
    </td>

    <td class="text-right">
      {{ australianCurrency($props.packageTradeItem.trade_item.rate) }}
    </td>

    <td class="text-right">
      {{ australianCurrency($props.packageTradeItem.trade_item.budget) }}
    </td>

    <td class="text-right">
      <div
        v-if="isEditing"
        class="flex justify-end items-center space-x-1"
      >
        <AppInput
          v-model="packageTradeItemForm.form.awarded_works"
          name="awarded_works"
          type="number"
          min="0"
          max="100"
          step="0.01"
          class="form-input w-20 p-1 text-right"
        />

        <span class="text-xs">%</span>
      </div>

      <template v-else>
        {{ $props.packageTradeItem.awarded_works ? `${australianNumber($props.packageTradeItem.awarded_works)}%` : '' }}
      </template>
    </td>

    <td
      v-if="isEditing && $props.package.procurement_method === 'schedule_of_rates'"
      class="text-right"
    >
      <div class="flex justify-end items-center space-x-1">
        <AppInput
          v-model="packageTradeItemForm.form.quantity"
          name="quantity"
          type="number"
          class="form-input w-16 p-1"
        />

        <span class="text-xs">
          ({{ $props.packageTradeItem.trade_item.unit }})
        </span>
      </div>
    </td>

    <td
      v-else
      class="text-right"
      :class="{ 'text-red-600': $props.packageTradeItem.quantity && $props.packageTradeItem.trade_item.$awarded_quantity !== $props.packageTradeItem.trade_item.quantity }"
      :title="$props.packageTradeItem.quantity && $props.packageTradeItem.trade_item.$awarded_quantity !== $props.packageTradeItem.trade_item.quantity ? $t('Subcontract.PackagesDashboard.PackageTradeItemRow.message.quantityMismatchTooltip') : ''"
    >
      {{ $props.packageTradeItem.quantity ? `${australianNumber($props.packageTradeItem.quantity)} (${$props.packageTradeItem.trade_item.unit})` : '' }}
    </td>

    <td v-if="isEditing && $props.package.procurement_method === 'schedule_of_rates'">
      <AppCurrencyInput
        v-model="packageTradeItemForm.form.rate"
        name="rate"
        class="ml-auto form-input w-16 p-1"
      />
    </td>

    <td
      v-else
      class="text-right"
    >
      {{ $props.packageTradeItem.rate ? australianCurrency($props.packageTradeItem.rate) : '' }}
    </td>

    <td v-if="isEditing && $props.package.procurement_method === 'lump_sum'">
      <AppCurrencyInput
        v-model="packageTradeItemForm.form.value"
        name="value"
        :disabled="packageTradeItemForm.form.rate > 0 && packageTradeItemForm.form.quantity > 0"
        class="disabled:bg-gray-300 form-input w-16 p-1 ml-auto"
      />
    </td>

    <td
      v-else-if="isEditing"
      class="text-right"
    >
      {{ australianCurrency(packageTradeItemForm.form.value) }}
    </td>

    <td
      v-else
      class="text-right"
    >
      {{ australianCurrency($props.packageTradeItem.value) }}
    </td>

    <td class="text-right">
      <p :class="[rowProfit < 0 ? 'text-red-600' : 'text-green-600']">
        {{ australianCurrency(rowProfit) }}
      </p>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber } from '@/helpers';
import { t } from '@/i18n';
import Package from '@/models/Package';
import PackageTradeItem from '@/models/PackageTradeItem';
import WbsCode from '@/models/WbsCode';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    packageTradeItem: PackageTradeItem;
    showEditButtons: boolean;
    package: Package;
    scrollableContainer: HTMLElement;
    depth?: number;
  }>(),
  {
    depth: 0,
  },
);

const emits = defineEmits<{
  (e: 'delete');
}>();

const isEditing = ref(false);
const packageTradeItemsStore = usePackageTradeItemsStore();
const projectsStore = useProjectsStore();

const packageTradeItemForm = useFormData({
  rate: undefined,
  quantity: undefined,
  value: undefined,
  wbs_code_id: undefined,
  awarded_works: undefined,
});

const updatePackageTradeItemAction = useStoreApiAction(packageTradeItemsStore.update);

const canSubmitForm = computed(() => {
  return (packageTradeItemForm.form.rate && packageTradeItemForm.form.quantity) || packageTradeItemForm.form.value;
});

// WBS Code editing computed properties
const project = computed(() => {
  return projectsStore.project;
});

watch(() => [packageTradeItemForm.form.rate, packageTradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] > 0 && newValue[1] > 0) {
    packageTradeItemForm.form.value = newValue[0] * newValue[1];
  }
});

const wbsCodesStore = useWbsCodesStore();
const wbsCodeSearch = ref(props.packageTradeItem.wbs_code?.code || '');

const edit = () => {
  isEditing.value = true;

  wbsCodeSearch.value = props.packageTradeItem.wbs_code?.code || '';
  packageTradeItemForm.setData(props.packageTradeItem);
};

const updatePackageTradeItem = () => {
  if(canSubmitForm.value) {
    updatePackageTradeItemAction.request(props.packageTradeItem.id, packageTradeItemForm.form)
      .then((packageTradeItem) => {
        isEditing.value = false;
      }).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('Subcontract.PackagesDashboard.PackageTradeItemRow.message.failedToUpdate'),
          text: error?.data?.message,
        });
      });
  }
};

watch(wbsCodeSearch, (newValue) => {
  if(!newValue.trim()) {
    packageTradeItemForm.form.wbs_code_id = undefined;
  }
});

const filteredWbsCodes = computed(() => {
  const searchTerm = wbsCodeSearch.value.toLowerCase();

  let wbsCodes = wbsCodesStore.models
    .where('project_id', project.value.id);

  if(props.package.procurement_method === 'schedule_of_rates') {
    wbsCodes = wbsCodes.where('unit', props.packageTradeItem.trade_item.unit);
  }

  if(searchTerm.trim()) {
    wbsCodes = wbsCodes
      .where((wbsCode) => {
        return wbsCode.code.toLowerCase().includes(searchTerm) ||
          wbsCode.description.toLowerCase().includes(searchTerm);
      });
  }

  return wbsCodes.get();
});

const getSuggestions = () => {
  return filteredWbsCodes.value.map((wbsCode: WbsCode) => ({
    label: `${wbsCode.code} - ${wbsCode.unit}`,
    value: wbsCode.id,
  }));
};

const getInitialSuggestions = () => {
  return filteredWbsCodes.value
    .map((wbsCode: WbsCode) => ({
      label: `${wbsCode.code} - ${wbsCode.unit}`,
      value: wbsCode.id,
    }));
};

const assignWbsCode = ({ index, value }: { index: number; value: { label: string; value: unknown } }) => {
  packageTradeItemForm.form.wbs_code_id = value.value as Id;
};

const rowProfit = computed(() => {
  return props.packageTradeItem.awarded_works && props.packageTradeItem.value ?
    (props.packageTradeItem.trade_item.budget * (props.packageTradeItem.awarded_works / 100)) -
    props.packageTradeItem.value :
    0;
});
</script>

<style scoped></style>
